import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { LanguageSchema } from "./column-schemas.js";
const IntCsvList = S.transform(S.String, S.Array(S.Int), {
    strict: true,
    decode: (value) => value
        .split(",")
        .map((item) => Number(item.trim()))
        .filter(Number.isInteger),
    encode: (items) => items.join(","),
});
/**
 * The risk-catalog value tables (`risk_template_areas`/`objects`/
 * `activities`/`sources`/`modes`/`tags`) are shared, admin-only lookup lists
 * a risk template's area/object/.../mode fields resolve through — gated the
 * same way as `admin-risk-templates`. `risk_template_factors` is listable
 * here (the risk-center's count column needs it) but not writable through
 * the generic value endpoints — factor rows carry subfactor children the
 * dedicated factor/subfactor endpoints below manage.
 */
export const RiskCatalogTable = S.Literal("risk_template_areas", "risk_template_objects", "risk_template_activities", "risk_template_factors", "risk_template_sources", "risk_template_modes", "risk_template_tags");
export const WritableRiskCatalogTable = S.Literal("risk_template_areas", "risk_template_objects", "risk_template_activities", "risk_template_sources", "risk_template_modes", "risk_template_tags");
export const RiskCatalogValueRow = S.Struct({
    id: S.Int,
    language: LanguageSchema,
    translation_group_id: S.NullOr(S.Int),
    value: S.String,
});
export const RiskCatalogValueNotFound = S.Struct({
    _tag: S.Literal("RiskCatalogValueNotFound"),
});
/** The value's translation group is still referenced by a risk template — deleting it would orphan that reference. */
export const RiskCatalogValueInUse = S.Struct({
    _tag: S.Literal("RiskCatalogValueInUse"),
});
export const ListRiskCatalogValuesParams = S.extend(PagingParams, S.Struct({
    table: RiskCatalogTable,
    language: S.optional(LanguageSchema),
    search: S.optional(S.String),
    translation_group_ids: S.optional(IntCsvList),
}));
export const ListRiskCatalogValues = HttpApiEndpoint.get("listRiskCatalogValues", "/admin/risk-catalog-values")
    .setUrlParams(ListRiskCatalogValuesParams)
    .addSuccess(pagedResponse(RiskCatalogValueRow))
    .addError(Forbidden, { status: 403 });
export const CreateRiskCatalogValuePayload = S.Struct({
    table: WritableRiskCatalogTable,
    language: LanguageSchema,
    value: S.NonEmptyString,
    translation_group_id: S.optional(S.Int),
});
export const CreateRiskCatalogValue = HttpApiEndpoint.post("createRiskCatalogValue", "/admin/risk-catalog-values/create")
    .setPayload(CreateRiskCatalogValuePayload)
    .addSuccess(S.Struct({ data: RiskCatalogValueRow }))
    .addError(Forbidden, { status: 403 });
export const UpdateRiskCatalogValuePayload = S.Struct({
    table: WritableRiskCatalogTable,
    id: S.Int,
    value: S.NonEmptyString,
});
export const UpdateRiskCatalogValue = HttpApiEndpoint.post("updateRiskCatalogValue", "/admin/risk-catalog-values/update")
    .setPayload(UpdateRiskCatalogValuePayload)
    .addSuccess(S.Struct({ data: RiskCatalogValueRow }))
    .addError(RiskCatalogValueNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteRiskCatalogValuePayload = S.Struct({
    table: WritableRiskCatalogTable,
    id: S.Int,
});
export const DeleteRiskCatalogValue = HttpApiEndpoint.post("deleteRiskCatalogValue", "/admin/risk-catalog-values/delete")
    .setPayload(DeleteRiskCatalogValuePayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(RiskCatalogValueNotFound, { status: 404 })
    .addError(RiskCatalogValueInUse, { status: 409 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// risk_template_laws — a distinct shape (title/description/external_link),
// referenced by templates through risk_template_law_links rather than a
// translation-group foreign key column.
// ---------------------------------------------------------------------------
export const RiskCatalogLawRow = S.Struct({
    id: S.Int,
    language: LanguageSchema,
    translation_group_id: S.NullOr(S.Int),
    title: S.String,
    description: S.String,
    external_link: S.NullOr(S.String),
});
export const RiskCatalogLawNotFound = S.Struct({
    _tag: S.Literal("RiskCatalogLawNotFound"),
});
/** The law is still linked to a risk template via `risk_template_law_links` — deleting it would orphan that link. */
export const RiskCatalogLawInUse = S.Struct({
    _tag: S.Literal("RiskCatalogLawInUse"),
});
export const ListRiskCatalogLawsParams = S.extend(PagingParams, S.Struct({
    language: S.optional(LanguageSchema),
    search: S.optional(S.String),
    translation_group_ids: S.optional(IntCsvList),
}));
export const ListRiskCatalogLaws = HttpApiEndpoint.get("listRiskCatalogLaws", "/admin/risk-catalog-laws")
    .setUrlParams(ListRiskCatalogLawsParams)
    .addSuccess(pagedResponse(RiskCatalogLawRow))
    .addError(Forbidden, { status: 403 });
export const CreateRiskCatalogLawPayload = S.Struct({
    language: LanguageSchema,
    title: S.NonEmptyString,
    description: S.NonEmptyString,
    external_link: S.optional(S.NullOr(S.String)),
    translation_group_id: S.optional(S.Int),
});
export const CreateRiskCatalogLaw = HttpApiEndpoint.post("createRiskCatalogLaw", "/admin/risk-catalog-laws/create")
    .setPayload(CreateRiskCatalogLawPayload)
    .addSuccess(S.Struct({ data: RiskCatalogLawRow }))
    .addError(Forbidden, { status: 403 });
export const UpdateRiskCatalogLawPayload = S.Struct({
    id: S.Int,
    title: S.optional(S.NonEmptyString),
    description: S.optional(S.NonEmptyString),
    external_link: S.optional(S.NullOr(S.String)),
});
export const UpdateRiskCatalogLaw = HttpApiEndpoint.post("updateRiskCatalogLaw", "/admin/risk-catalog-laws/update")
    .setPayload(UpdateRiskCatalogLawPayload)
    .addSuccess(S.Struct({ data: RiskCatalogLawRow }))
    .addError(RiskCatalogLawNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteRiskCatalogLawPayload = S.Struct({ id: S.Int });
export const DeleteRiskCatalogLaw = HttpApiEndpoint.post("deleteRiskCatalogLaw", "/admin/risk-catalog-laws/delete")
    .setPayload(DeleteRiskCatalogLawPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(RiskCatalogLawNotFound, { status: 404 })
    .addError(RiskCatalogLawInUse, { status: 409 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// risk_template_factors / risk_template_subfactors — the one catalog pair
// with a parent-child shape: each subfactor row hangs off one factor row
// (per language) via `factor_id`, and deleting a factor cascades to its
// subfactors. The dedicated factor editor manages both through these
// endpoints; the generic value endpoints stay factor-read-only.
// ---------------------------------------------------------------------------
export const RiskCatalogLanguage = LanguageSchema;
export const RiskSubfactorRow = S.Struct({
    id: S.Int,
    factor_id: S.Int,
    language: LanguageSchema,
    translation_group_id: S.NullOr(S.Int),
    value: S.String,
});
export const RiskFactorWithSubfactorsRow = S.extend(RiskCatalogValueRow, S.Struct({ subfactors: S.Array(RiskSubfactorRow) }));
export const RiskFactorNotFound = S.Struct({
    _tag: S.Literal("RiskFactorNotFound"),
});
/**
 * The factor's translation group — or a child subfactor's, which the delete
 * would cascade away — is still referenced by a risk template or instance.
 */
export const RiskFactorInUse = S.Struct({
    _tag: S.Literal("RiskFactorInUse"),
});
export const RiskSubfactorNotFound = S.Struct({
    _tag: S.Literal("RiskSubfactorNotFound"),
});
/** The subfactor's translation group is still referenced by a risk template or instance. */
export const RiskSubfactorInUse = S.Struct({
    _tag: S.Literal("RiskSubfactorInUse"),
});
export const ListRiskFactorsParams = S.extend(PagingParams, S.Struct({
    language: S.optional(LanguageSchema),
    search: S.optional(S.String),
}));
export const ListRiskFactors = HttpApiEndpoint.get("listRiskFactors", "/admin/risk-factors")
    .setUrlParams(ListRiskFactorsParams)
    .addSuccess(pagedResponse(RiskFactorWithSubfactorsRow))
    .addError(Forbidden, { status: 403 });
export const CreateRiskFactorPayload = S.Struct({
    language: RiskCatalogLanguage,
    value: S.NonEmptyString,
    translation_group_id: S.optional(S.Int),
});
export const CreateRiskFactor = HttpApiEndpoint.post("createRiskFactor", "/admin/risk-factors/create")
    .setPayload(CreateRiskFactorPayload)
    .addSuccess(S.Struct({ data: RiskCatalogValueRow }))
    .addError(Forbidden, { status: 403 });
export const UpdateRiskFactorPayload = S.Struct({
    id: S.Int,
    value: S.NonEmptyString,
});
export const UpdateRiskFactor = HttpApiEndpoint.post("updateRiskFactor", "/admin/risk-factors/update")
    .setPayload(UpdateRiskFactorPayload)
    .addSuccess(S.Struct({ data: RiskCatalogValueRow }))
    .addError(RiskFactorNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteRiskFactorPayload = S.Struct({ id: S.Int });
export const DeleteRiskFactor = HttpApiEndpoint.post("deleteRiskFactor", "/admin/risk-factors/delete")
    .setPayload(DeleteRiskFactorPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(RiskFactorNotFound, { status: 404 })
    .addError(RiskFactorInUse, { status: 409 })
    .addError(Forbidden, { status: 403 });
export const CreateRiskSubfactorPayload = S.Struct({
    factor_id: S.Int,
    language: RiskCatalogLanguage,
    value: S.NonEmptyString,
    translation_group_id: S.optional(S.Int),
});
export const CreateRiskSubfactor = HttpApiEndpoint.post("createRiskSubfactor", "/admin/risk-subfactors/create")
    .setPayload(CreateRiskSubfactorPayload)
    .addSuccess(S.Struct({ data: RiskSubfactorRow }))
    .addError(RiskFactorNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const UpdateRiskSubfactorPayload = S.Struct({
    id: S.Int,
    value: S.NonEmptyString,
});
export const UpdateRiskSubfactor = HttpApiEndpoint.post("updateRiskSubfactor", "/admin/risk-subfactors/update")
    .setPayload(UpdateRiskSubfactorPayload)
    .addSuccess(S.Struct({ data: RiskSubfactorRow }))
    .addError(RiskSubfactorNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteRiskSubfactorPayload = S.Struct({ id: S.Int });
export const DeleteRiskSubfactor = HttpApiEndpoint.post("deleteRiskSubfactor", "/admin/risk-subfactors/delete")
    .setPayload(DeleteRiskSubfactorPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(RiskSubfactorNotFound, { status: 404 })
    .addError(RiskSubfactorInUse, { status: 409 })
    .addError(Forbidden, { status: 403 });
export const adminRiskCatalogValuesGroup = HttpApiGroup.make("admin-risk-catalog-values")
    .add(ListRiskCatalogValues)
    .add(CreateRiskCatalogValue)
    .add(UpdateRiskCatalogValue)
    .add(DeleteRiskCatalogValue)
    .add(ListRiskCatalogLaws)
    .add(CreateRiskCatalogLaw)
    .add(UpdateRiskCatalogLaw)
    .add(DeleteRiskCatalogLaw)
    .add(ListRiskFactors)
    .add(CreateRiskFactor)
    .add(UpdateRiskFactor)
    .add(DeleteRiskFactor)
    .add(CreateRiskSubfactor)
    .add(UpdateRiskSubfactor)
    .add(DeleteRiskSubfactor)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-risk-catalog-values.js.map